import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Phone, Mail, MapPin, MessageSquare, Trash2, Loader2, Send, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, withAuthUrl, formatApiErrorDetail } from "@/lib/api";
import { STATUSES, STATUS_COLORS } from "@/pages/admin/Leads";
import { useServices } from "@/lib/hooks";

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [converting, setConverting] = useState(false);
  const { data: services = [] } = useServices();

  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => (await api.get(`/admin/leads/${id}`)).data,
  });

  if (isLoading) return <p className="text-slate-500">Loading…</p>;
  if (!lead) return <p className="text-slate-500">Lead not found.</p>;

  const serviceNames = lead.service_ids.map((sid) => services.find((s) => s.id === sid)?.name).filter(Boolean);

  const changeStatus = async (status) => {
    await api.patch(`/admin/leads/${id}/status`, { status });
    qc.invalidateQueries({ queryKey: ["lead", id] });
    qc.invalidateQueries({ queryKey: ["leads"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    toast.success(`Status updated to ${status}`);
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/admin/leads/${id}/notes`, { note });
      setNote("");
      qc.invalidateQueries({ queryKey: ["lead", id] });
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingNote(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await api.delete(`/admin/leads/${id}`);
    qc.invalidateQueries({ queryKey: ["leads"] });
    toast.success("Lead deleted");
    navigate("/admin/leads");
  };

  const convert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/admin/leads/${id}/convert-to-invoice`);
      toast.success(`Created ${data.invoice_number}`);
      navigate(`/admin/invoices/${data.id}/edit`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setConverting(false);
    }
  };

  return (
    <div data-testid="lead-detail-page">
      <Link to="/admin/leads" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-semibold"><ArrowLeft className="h-4 w-4" /> Back to leads</Link>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-slate-900 text-2xl sm:text-3xl">{lead.full_name}</h1>
          <p className="text-slate-400 text-sm mt-1">Received {new Date(lead.created_at).toLocaleString()}</p>
        </div>
        <button onClick={remove} data-testid="lead-delete" className="shrink-0 h-10 w-10 rounded-xl bg-red-50 text-red-600 grid place-items-center hover:bg-red-100"><Trash2 className="h-5 w-5" /></button>
      </div>

      <button onClick={convert} disabled={converting} data-testid="lead-convert-invoice" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-60">
        {converting ? <Loader2 className="h-4.5 w-4.5 animate-spin" /> : <Receipt className="h-4.5 w-4.5" />} Create invoice from this lead
      </button>

      {/* Status tracker */}
      <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
        <div className="text-sm font-semibold text-slate-700 mb-3">Status</div>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => changeStatus(s)} data-testid={`status-btn-${s.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-bold transition ${lead.status === s ? STATUS_COLORS[s] + " ring-2 ring-offset-1 ring-green-400" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Contact + details */}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm space-y-3">
          <div className="text-sm font-semibold text-slate-700">Contact</div>
          <a href={`tel:${lead.phone}`} className="flex items-center gap-3 text-slate-700 hover:text-green-700" data-testid="lead-call"><Phone className="h-4.5 w-4.5 text-green-600" /> {lead.phone}</a>
          {lead.email && <a href={`mailto:${lead.email}`} className="flex items-center gap-3 text-slate-700 hover:text-green-700 break-all" data-testid="lead-email"><Mail className="h-4.5 w-4.5 text-green-600" /> {lead.email}</a>}
          {(lead.address || lead.postcode) && <div className="flex items-center gap-3 text-slate-700"><MapPin className="h-4.5 w-4.5 text-green-600" /> {[lead.address, lead.postcode].filter(Boolean).join(", ")}</div>}
          <div className="text-sm text-slate-500 pt-2 border-t border-slate-100">Preferred contact: <span className="font-semibold text-slate-700 capitalize">{lead.preferred_contact_method}</span></div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
          <div className="text-sm font-semibold text-slate-700 mb-2">Requested services</div>
          {serviceNames.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {serviceNames.map((n) => <span key={n} className="rounded-full bg-green-50 px-3 py-1 text-sm font-semibold text-green-700">{n}</span>)}
            </div>
          ) : <p className="text-slate-400 text-sm">None specified</p>}
          {lead.message && <div className="mt-4"><div className="text-sm font-semibold text-slate-700 mb-1">Message</div><p className="text-slate-600 text-sm leading-relaxed">{lead.message}</p></div>}
          {lead.photo_url && (
            <div className="mt-4">
              <div className="text-sm font-semibold text-slate-700 mb-2">Attached photo</div>
              <img src={withAuthUrl(lead.photo_url)} alt="Lead attachment" className="rounded-xl max-h-64 border border-slate-200" data-testid="lead-photo" />
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3"><MessageSquare className="h-4.5 w-4.5" /> Internal notes</div>
        <div className="flex gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add a note…" data-testid="note-input"
            className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100" />
          <button onClick={addNote} disabled={savingNote} data-testid="note-add" className="rounded-xl bg-green-600 px-4 text-white hover:bg-green-700 disabled:opacity-60 grid place-items-center">
            {savingNote ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
        <ul className="mt-4 space-y-2">
          {(lead.internal_notes || []).slice().reverse().map((n) => (
            <li key={n.id} className="rounded-xl bg-slate-50 px-4 py-3" data-testid="note-item">
              <p className="text-slate-700 text-sm">{n.note}</p>
              <p className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
            </li>
          ))}
          {(!lead.internal_notes || lead.internal_notes.length === 0) && <p className="text-slate-400 text-sm">No notes yet.</p>}
        </ul>
      </div>
    </div>
  );
}
